<template>
  <div class="ba-targeting-list-wrap"
      :class="{
        'forward': isForwardDirection,
        'backward': !isForwardDirection
      }"
      :style="{
        'width': listWrapWidth + 'px'
      }">
    <div class="ba-targeting-list-bg"
        :style="{
          'width': listWidth + 'px',
          'height': Math.max(paneWrapSize[1], listHeight + listTranslateY) + paneExtraPadding[0] * 2 + 'px'
        }">
      <template>
        <div class="more-seq-wrap" v-if="moreSeq" @click="$emit('get-seq')">
          <div class="more-btn-background"></div>
          <div class="more-seq-btn" :style="{
                'transform': `translate3d(-50%, ${ moreButtonY }px, 0)`
              }" >
              <i class="icon size-20" :class="`icon--plus-${isForwardDirection ? 'forward' : 'backward'}`"></i>
              More
          </div>
        </div>
      </template>
      <div class="grid-backgrounds">
        <template v-for="(sequence, sequenceIndex) in new Array(maxDisplayIndex + 1)">
          <template v-if="sequenceIndex > 0">
            <span :key="sequenceIndex"
                  :class="{'focused': focusedGridIndex === sequenceIndex}" />
          </template>
        </template>
      </div>
      <div v-if="isInitialized"
          class="ba-targeting-list"
          :class="{'hovered': isCurrentNodeHovered, 'heatmap': buttonHover}"
          :style="{
            'height': listHeight + 'px',
            'padding-bottom': paneWrapSize[1] - paneExtraPadding[0] + 50 + 'px',
            'transform': `translate3d(0, ${ listTranslateY }px, 0)`
          }">
        <div v-if="!noData"
            class="switch-direction"
            :class="`${ device }`"
            :style="{
              'z-index': numCroppedDisplayableSequences + 3
            }">
          <button type="button"
                  @click="switchDirection">
            {{ $t('journey.targeting.' + (isForwardDirection ? 'backwardDirection' : 'forwardDirection')) }}
          </button>
          <ui-tooltip arrowBottom
                      arrowCenter>
            <span v-html="$t('journey.targeting.' + (isForwardDirection ? 'backwardDirection' : 'forwardDirection'))" />
          </ui-tooltip>
        </div>
        <div class="targeting-info"
            :class="[{'empty': noData}, device]"
            :style="{
              'z-index': noData ? '' : numCroppedDisplayableSequences + 2
            }">
          <button class="ai-report-button" :disabled="!ableToShowAiReport" @click="onClickAiReport">
            <i class="icon icon--ai-blue" />
            <span class="text-contents">Report</span>
          </button>
          <h3>{{ $t('common.targets') }}</h3>
          <ba-node-link v-if="currentNodeInfo"
                        :pageData="currentNodeInfo"
                        :isCustomNode="isCustomNode"
                        :displayBackLetters="false" />
          <button type="button"
                  class="setting-button"
                  :class="{
                    'is-mobile': isMobile
                  }"
                  @click="onSettingButtonClick">
            <i class="icon icon--setting-size16"></i>
            Setting
            <ui-tooltip arrowCenter>
              <span v-html="$t('journey.targeting.setting')"></span>
            </ui-tooltip>
          </button>
          <ui-selectbox v-if="isForwardDirection"
                        :width="196"
                        :height="28"
                        :value="lookupOption"
                        :items="lookupOptions"
                        :disabled="noData"
                        @input="onLookupOptionChange" />
        </div>

        <div v-if="noData"
            class="current-node empty"
            :class="`${ journeyOptions.device } ${ $i18n.locale }`"
            :style="{
              '--current-node-cover-color': currentNodeCoverColor
            }">
          <p class="ment" v-html="$t('journey.targeting.noData.title')" />
          <!-- <p class="ment" v-html="'The targeting journey <br>does not exist.'" /> -->
          <!-- <p class="ment" v-html="'ターゲティング旅程は<br>存在しません。'" /> -->
          <p class="info">
            <i class="icon icon--info-outline-size17" />
            <ui-tooltip arrowBottom
                        arrowCenter>
              <span v-html="$t('journey.targeting.noData.description')" />
            </ui-tooltip>
          </p>
          <ba-targeting-node-values :nodeData="currentNodeData"
                                    :visibleOptions="visibleOptions"
                                    :numSelectedVisibleOptions="numSelectedVisibleOptions"
                                    :isForwardDirection="isForwardDirection" />
        </div>
        <div v-else
            class="current-node"
            :class="[
              device,
              {
                'hovered': isCurrentNodeHovered,
                'bookmarked': currentNodeInfo.bookmark,
                'active': reportShowing && focusedSequenceIndex === 0,
                'no-thumbnail': !currentNodeInfo.thumbnail,
                'trackable': isTargetTrackable,
                'tracking': tracking
              }
            ]"
            :style="{
              'z-index': numCroppedDisplayableSequences + 1,
              '--current-node-cover-color': currentNodeCoverColor
            }"
            @mouseenter.prevent="isCurrentNodeHovered = true"
            @mouseleave.prevent="isCurrentNodeHovered = false"

            @click="onCurrentNodeClick">

          <template v-if="!isCustomNode">
            <span class="thumbnail"
                  :style="{
                  'background-image': 'url(' + (currentNodeInfo.thumbnail || defaultThumbnail)
                }" />
            <heatmap-sample v-if="isShowHeatmap" :device="device"></heatmap-sample>
            <transition name="group-name">
              <span v-if="grouping"
                    class="group-name"
                    :class="{
                      'untagged': currentNodeGroupName === $t('group.upcoming')
                    }">
                <span>{{ currentNodeGroupName }}</span>
                <i class="icon icon--upcoming-large"></i>
              </span>
            </transition>
          </template>
          <template v-else>
            <span
                class="group-name"
                :class="{
                  'untagged': currentNodeInfo.customId === 0
                }"
                :style="{'background-color': currentNodeInfo.customColor}"
            >
              <span>{{ currentNodeInfo.customId === 0 ? $t('customTag.untaggedLabel') : currentNodeInfo.customValue}}</span>
              <i class="icon icon--upcoming-large"></i>
            </span>
          </template>
          <ba-targeting-node-values :nodeData="currentNodeData"
                                    :visibleOptions="visibleOptions"
                                    :numSelectedVisibleOptions="numSelectedVisibleOptions"
                                    :isForwardDirection="isForwardDirection" />
          <ba-targeting-dropoff-path v-if="currentNodeData._dropPathInfo"
                                    :nodeData="currentNodeData" />
          <template v-if="isTargetTrackable">
            <div class="tracking-box"
                v-if="tracking"
                @mouseenter="isCurrentNodeHovered = false; isTrackingHovered = true"
                @mouseleave="isCurrentNodeHovered = true; isTrackingHovered = false"
                @click.stop="$emit('onShowTrackingLayer', 0, 0)">
              <div class="tracking-box-header">
                <div class="tracking-label-wrap">
                  <ba-tracking-label
                      :is-static="true"
                      :is-hovered="isTrackingHovered"
                      :icon="isTrackingHovered ? 'icon--tracking-white' : 'icon--tracking-default'"
                      @click="$emit('onShowTrackingLayer', 0, 0)" />
                  <h4>..Tracking</h4>
                </div>
                <p class="tracking-user">
                  <span class="tracking-user-label">{{ trackingInfoText.label }}</span>
                  <span class="tracking-user-value">{{ trackingInfoText.value }}</span>
                </p>
              </div>
              <ba-tracking-bar
                :total="currentNodeData.sessCnt"
                :member="currentNodeData.memberSessCnt"
                :login="currentNodeData.loggedInSessCnt"
                :newLogin="currentNodeData.newlyLoggedInSessCnt"
                @onClick="$emit('onShowTrackingLayer', 0, 0)"
              />
              <button class="stop-tracking-button"
                  v-if="tracking"
                  @mouseenter="isTrackingHovered = false"
                  @mouseleave="isTrackingHovered = true"
                  @click.stop="$emit('onStopTracking')">
                <i class="icon icon--close14 size-12"></i>
              </button>
            </div>
            <ba-tracking-bar v-else
                :total="currentNodeData.sessCnt"
                :member="currentNodeData.memberSessCnt"
                :login="currentNodeData.loggedInSessCnt"
                :newLogin="currentNodeData.newlyLoggedInSessCnt"
                @onMouseEnter="isCurrentNodeHovered = false"
                @onMouseLeave="isCurrentNodeHovered = true"
                @onClick="$emit('onShowTrackingLayer', 0, 0)" />
          </template>
          <div v-if="nodesDataArray.length < 2" class="no-journey">
            <p class="ment">
              <span v-html="$t('journey.targeting.no' + (isForwardDirection ? 'Forward' : 'Backward') + 'Journey')" />
            </p>
          </div>

          <div class="buttons">
            <div class="wrap">
              <button class="heatmap" :class="{'hovered': buttonHover}"
                      @click.stop="onHeatmap(currentNodeInfo, currentNodeData)"
                      @mouseenter.stop="buttonHover = true;"
                      @mouseleave.stop="buttonHover = false"
                      v-if="isShowHeatmap">
                <i class="icon icon--heatmap-on small"></i>
                <span class="label"> {{ $t('common.heatmap') }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="targeting-paths-wrap">
          <svg>
            <defs>
              <linearGradient x1="41.054%" y1="66.447%" x2="41.054%" y2="4.889%" id="dropped-line-linear-gradient">
                  <stop stop-color="#FACBA1" stop-opacity="0" offset="0%"/>
                  <stop stop-color="#FAC195" offset="58.843%"/>
                  <stop stop-color="#FD5C1A" offset="100%"/>
              </linearGradient>
            </defs>
          </svg>
          <template v-for="(sequenceData, sequenceIndex) in nodesDataArray">
            <template v-if="sequenceIndex > 0 && sequenceIndex <= maxDisplayIndex + 1">
              <transition :key="'path-svg-' + sequenceIndex"
                          name="appear-line">
                <svg v-if="sequenceIndex <= showIndex + 1"
                    class="targeting-paths"
                    :data-sequence-index="sequenceIndex"
                    :style="svgStyles[sequenceIndex]">
                  <defs>
                    <linearGradient x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="userSpaceOnUse"
                                    :id="'path-svg-gradient-' + sequenceIndex">
                      <stop offset="0%"
                            :stop-color="gradientColors[sequenceIndex][0]" />
                      <stop offset="100%"
                            :stop-color="gradientColors[sequenceIndex][1]" />
                    </linearGradient>
                  </defs>
                  <template v-for="(nodeData, nodeIndex) in sequenceData">
                    <template v-if="nodeData._parent._display && !nodeData._ignore">
                      <ba-targeting-node-path v-if="!(nodeData._combinedLineInfo && !nodeData._display)"
                                              :key="'path-' + sequenceIndex + '-' + nodeIndex"
                                              :sequenceIndex="sequenceIndex"
                                              :nodeIndex="nodeIndex"
                                              :lineInfo="nodeData._lineInfo"
                                              :data-test-cutted="nodeData._lineInfo.cutted"
                                              :data-test-ignore="nodeData._ignore ? 'true' : 'false'"
                                              :isInvisible="!nodeData._display" />
                      <ba-targeting-node-path v-if="nodeData._combinedLineInfo && maxDisplayIndex + 1 > sequenceIndex"
                                              :key="'combined-path-' + sequenceIndex + '-' + nodeIndex"
                                              :sequenceIndex="sequenceIndex"
                                              :nodeIndex="nodeIndex"
                                              :lineInfo="nodeData._combinedLineInfo"
                                              :isInvisible="!nodeData._display" />
                    </template>
                  </template>
                </svg>
              </transition>
            </template>
          </template>
        </div>

        <template v-for="(sequenceData, sequenceIndex) in nodesDataArray">
          <template v-if="sequenceIndex > 0 && sequenceIndex <= maxDisplayIndex">
            <template v-for="(nodeData, nodeIndex) in sequenceData">
              <transition :key="'node-' + sequenceIndex + '-' + nodeIndex"
                          :css="!disableTransition"
                          name="appear-node">
                <template v-if="sequenceIndex <= showIndex && nodeData._display">
                  <template v-if="nodeData._hash === ''">
                    <div :key="`more-rank-${sequenceIndex}`" class="more-rank-btn" :class="device"
                      :style="{
                        top: `${nodeData._layout.y}px`,
                        right: isForwardDirection ? 'auto' : `${nodeData._layout.x}px`,
                        left: isForwardDirection ? `${nodeData._layout.x}px` : 'auto',
                        width: `${nodeData._layout.width}px`,
                        height: `${nodeData._layout.height}px`
                      }" @click="$emit('get-rank')">
                      <i class="icon size-20" :class="`icon--plus-${isForwardDirection ? 'forward' : 'backward'}`"></i>
                      More
                    </div>
                  </template>
                  <ba-targeting-node v-else :isForwardDirection="isForwardDirection"
                                    :isActive="reportShowing && (focusedSequenceIndex === sequenceIndex && focusedNodeIndex === nodeIndex)"
                                    :numCroppedDisplayableSequences="numCroppedDisplayableSequences"
                                    :sequenceIndex="sequenceIndex"
                                    :nodeIndex="nodeIndex"
                                    :nodeData="nodeData"
                                    :nodeInfo="nodesData.nodeMap[nodeData._id] || {}"
                                    :nodeWidth="nodeWidth"
                                    :nodeHeight="nodeHeight"
                                    :layout="nodeData._layout"
                                    :visibleOptions="visibleOptions"
                                    :numSelectedVisibleOptions="numSelectedVisibleOptions"
                                    :defaultThumbnail="defaultThumbnail"
                                    :isTrackable="isTargetTrackable"
                                    :tracking="tracking"
                                    :grouping="grouping"
                                    :group="group"
                                    :isCustomNode="isCustomNode"
                                    :useNullity="useNullity"
                                    @onNodeClick="onNodeClick"
                                    @onHeatmap="onHeatmap"
                                    @onShowTrackingLayer="$emit('onShowTrackingLayer', sequenceIndex, nodeIndex)"
                  />
                </template>

                {{nodesData.nodeMap[nodeData._id]}}
              </transition>
            </template>
            <transition :key="'cover-' + sequenceIndex"
                        name="cover"
                        @after-leave="onCoverAfterLeave(sequenceIndex)">
              <div v-if="showIndex < sequenceIndex"
                  class="cover"
                  :style="coverStyles[sequenceIndex]" />
            </transition>
          </template>
        </template>
      </div>
      <!-- <div v-if="isMoreButtonVisible"
          class="more-button-wrap"
          :class="{
            'disabled': numSequencesLimit <= maxDisplayIndex
          }"
          :style="{
            'transform': `translate3d(0, ${ moreButtonY }px, 0)`
          }">
        <button class="more-button"
                @click="displayMoreData">
          <i class="icon icon--more-x-gray"></i>
        </button>
        <p v-if="numSequencesLimit <= maxDisplayIndex"
          class="no-more">
          <i class="icon icon--info"></i>
          <span v-html="$t('journey.targeting.noMoreSequence', {max: numSequencesLimit})"></span>
        </p>
      </div> -->
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { onlySubdomain, hexToRgb, utcToLocalTimezone, thousand } from '@/libs/utils'
import { EventBus } from '@/libs/event-bus'
import browser from '@/libs/browser'
import UiSelectbox from '@/components/ui/UiSelectbox'
import UiTooltip from '@/components/ui/UiTooltip'
import BaNodeLink from '@/components/trend/NodeLink'
import BaTrackingLabel from '@/components/journey/TrackingLabel.vue'
import BaTrackingBar from '@/components/journey/TrackingBar.vue'
import BaTargetingNode from '@/components/targeting/NodeItem'
import BaTargetingNodePath from '@/components/targeting/NodePath'
import BaTargetingNodeValues from '@/components/targeting/NodeValues'
import BaTargetingDropoffPath from '@/components/targeting/NodeDropoffPath'
import { normalizeHeatmapData } from '@/libs/heatmap-data-normalizer'
import { HEATMAP_STATE } from '@/libs/constants'
import { differenceInDays, format } from 'date-fns'
import HeatmapSample from '@/components/heatmap/HeatmapSample.vue'

export default {
  name: 'BaTargetingList',
  components: {
    HeatmapSample,
    UiSelectbox,
    UiTooltip,
    BaNodeLink,
    BaTrackingLabel,
    BaTrackingBar,
    BaTargetingNode,
    BaTargetingNodePath,
    BaTargetingNodeValues,
    BaTargetingDropoffPath
  },
  props: {
    isForwardDirection: {
      type: Boolean,
      required: true
    },

    nodesData: {
      type: Object,
      required: true
    },
    nodesDataArray: {
      type: Array,
      required: true
    },
    maxDisplayIndex: {
      type: Number,
      required: true
    },
    numCroppedDisplayableSequences: {
      type: Number,
      required: true
    },
    numSequencesLimit: {
      type: Number,
      required: true
    },

    paneScale: {
      type: Number,
      required: true
    },
    paneWrapSize: {
      type: Array,
      required: true
    },
    paneExtraPadding: {
      type: Array,
      required: true
    },
    panePosition: {
      type: Array,
      required: true
    },

    gridWidth: {
      type: Number,
      required: true
    },

    lookupOption: {
      type: String,
      required: true
    },
    connectionStrength: {
      type: Number,
      required: true
    },
    reportShowing: {
      type: Boolean,
      required: true
    },
    nodeReporting: {
      type: Boolean,
      required: true
    },
    tracking: {
      default: null
    },
    isTrackable: {
      default: false
    },
    grouping: {
      type: Boolean,
      required: true
    },
    group: {
      type: String,
      required: true
    },
    visibleOptions: {
      type: Object,
      required: true
    },
    numSelectedVisibleOptions: {
      type: Number,
      required: true
    },
    focusedGridIndex: {
      type: Number,
      required: true
    },
    defaultThumbnail: {
      type: String
    },

    disableTransition: {
      type: Boolean,
      required: true
    },
    noData: {
      type: Boolean,
      required: true
    },
    isMoreButtonVisible: {
      type: Boolean,
      required: true
    },
    useNullity: {
      type: Boolean,
      default: false
    },
    moreRank: {
      type: Boolean,
      default: false
    },
    moreSeq: {
      type: Boolean,
      default: false
    },
    isCustomNode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isInitialized: false,
      isCurrentNodeHovered: false,
      isTrackingHovered: false,

      focusedSequenceIndex: -1,
      focusedNodeIndex: -1,

      listHeight: 0,

      gradientColors: [],
      svgStyles: [],
      coverStyles: [],

      showIndex: 0,

      buttonHover: false,

      isShowHeatmap: false,
      moreButtonInfo: {},
      moreButtonPath: {}
    }
  },
  computed: {
    ...mapState([
      'planData',
      'journeyOptions',
      'journeyPeriod',
      'journeyTargetingOptions',
      'domain'
    ]),
    lookupOptions () {
      return [
        {
          value: 'reaching',
          text: this.$t('journey.targeting.lookupOptions.reaching'),
          extraNode: {
            className: 'info',
            html: `<span class="tooltip">${this.$t('journey.targeting.lookupOptions.reachingDescription')}</span>`
          }
        },
        {
          value: 'landing',
          text: this.$t('journey.targeting.lookupOptions.landing'),
          extraNode: {
            className: 'info',
            html: `<span class="tooltip">${this.$t('journey.targeting.lookupOptions.landingDescription')}</span>`
          }
        }
      ]
    },
    device () {
      return this.journeyOptions.device || 'all'
    },
    currentNodeHasGroup () {
      return this.currentNodeInfo && this.currentNodeInfo.tagInfo && this.currentNodeInfo.tagInfo[this.group]
    },
    currentNodeGroupName () {
      return this.currentNodeHasGroup ? onlySubdomain(this.currentNodeInfo.tagInfo[this.group].tagName || this.$t('group.upcoming'), this.journeyOptions.domain) : this.$t('group.upcoming')
    },
    currentNodeCoverColor () {
      if (!this.noData && this.grouping) {
        const color = hexToRgb(this.currentNodeHasGroup ? this.currentNodeInfo.tagInfo[this.group].colorHex || '#bbb' : '#bbb')
        return 'rgba(' + color.r + ',' + color.g + ',' + color.b + ', 0.8)'
      }
      return 'rgba(0, 0, 0, 0.05)'
    },
    currentNodeData () {
      return this.nodesDataArray[0][0]
    },
    currentNodeInfo () {
      return this.nodesData.nodeMap[this.currentNodeData._id]
    },
    listWrapWidth () {
      return Math.max(this.paneWrapSize[0], this.listWidth + 164 + 200 + (this.isMoreButtonVisible ? 30 + (this.isForwardDirection ? 0 : 52) : 0)) + this.paneExtraPadding[3] * 2
    },
    listWidth () {
      return (this.maxDisplayIndex) * this.gridWidth + 425
    },
    listTranslateY () {
      return (32 + (112 - 32) * this.paneScale) * (1 / this.paneScale)
    },
    currentNodeWidth () {
      switch (this.device) {
        case 'all':
          return 180
        case 'tablet':
          return 130
        case 'phone':
          return 100
        default:
          return 200
      }
    },
    currentNodeHeight () {
      switch (this.device) {
        case 'all':
          return 180
        case 'desktop':
          return 125
        default:
          return 166
      }
    },
    nodeWidth () {
      switch (this.device) {
        case 'all':
          return 100
        case 'tablet':
          return 80
        case 'phone':
          return 60
        default:
          return 110
      }
    },
    nodeHeight () {
      switch (this.device) {
        case 'all':
          return 100
        case 'desktop':
          return 69
        default:
          return 110
      }
    },
    nodeBottomMargin () {
      return 50
    },
    moreButtonY () {
      return (-(this.panePosition[1] + this.paneExtraPadding[0]) + this.paneExtraPadding[0] + this.paneWrapSize[1] / 2) * (1 / this.paneScale)
    },
    isMobile () {
      return browser.isMobile
    },
    isHeatmapAvailableRange () {
      const start = this.journeyPeriod.start
      const end = this.journeyPeriod.end
      const startDate = new Date(start)
      const endDate = new Date(end)

      const diffDay = differenceInDays(endDate, startDate)
      return diffDay <= 31
    },
    isTargetTrackable () {
      return this.isTrackable && this.currentNodeData.sessCnt > 0
    },
    trackingInfoText () {
      const trackingInfo = {}
      if (this.tracking) {
        if (this.tracking.type === 'guest') {
          trackingInfo.value = this.$t('tracking.guest')
        } else {
          if (this.tracking.selectedMembers && this.currentNodeData.memberCnt === 1) {
            trackingInfo.value = this.tracking.selectedMembers[0].displayId
          } else {
            trackingInfo.label = this.$t('tracking.member')
            trackingInfo.value = thousand(this.currentNodeData.memberCnt)
          }
        }
      }
      return trackingInfo
    },
    ableToShowAiReport () {
      return (!this.domain.shared || this.domain.shared === 'share') && this.journeyTargetingOptions.direction === 'backward' && !this.isCustomNode && !this.tracking && this.journeyOptions.device
    }
  },
  watch: {
    nodesDataArray (data) {
      if (data) {
        this.isInitialized = false
        this.listHeight = 0
        this.calculateNodePosition()
        this.generateGradient()
      }
    },
    connectionStrength (value) {
      this._nodePositionCalculated = true
      this.calculateNodePosition()
      this.$nextTick(() => {
        this._nodePositionCalculated = false
      })
    },
    maxDisplayIndex (index) {
      if (!this._nodePositionCalculated) {
        this.calculateNodePosition()
        this._nodePositionCalculated = false
      }
    },
    numSelectedVisibleOptions () {
      this.calculateNodePosition()
    },
    reportShowing (value) {
      if (!value) {
        this.focusedSequenceIndex = this.focusedNodeIndex = -1
      }
    }
  },
  created () {
    this.nodesData.nodeMap[this.currentNodeData._id] = normalizeHeatmapData(this.nodesData.nodeMap[this.currentNodeData._id])
    this.onCheckHeatmap()
  },
  mounted () {
    this.generateGradient()
    this.calculateNodePosition()
  },
  updated () {
  },
  beforeDestroy () {
  },
  methods: {
    onCheckHeatmap () {
      if (this.currentNodeData.cnt <= 0) {
        this.isShowHeatmap = false
        return false
      }
      if (!this.isHeatmapAvailableRange) {
        this.isShowHeatmap = false
        return false
      }

      if (!this.currentNodeInfo.baApply || !this.currentNodeInfo.heatmapAid) {
        this.isShowHeatmap = false
        return false
      }

      if (this.currentNodeInfo.heatmapUiState === HEATMAP_STATE.ANALYZING) {
        this.isShowHeatmap = false
        return false
      }

      const installDate = this.currentNodeInfo.baApply.installDate
      if (installDate) {
        const installDateStr = format(utcToLocalTimezone(installDate, this.domain.timezoneName), 'YYYY-MM-DD')

        if (this.journeyPeriod.end < installDateStr) {
          this.isShowHeatmap = false
          return false
        }
      } else {
        this.isShowHeatmap = false
        return false
      }

      if (this.currentNodeInfo.isExistHeatmap) {
        this.isShowHeatmap = true
      }
    },
    onHeatmap (pageData, nodeData) {
      if (nodeData) {
        pageData = {
          ...pageData,
          ...nodeData,
          targetNodeId: this.journeyTargetingOptions.targetNodeId
        }
      }

      this.$emit('onHeatmap', pageData)
    },
    show () {
      if (this.disableTransition) {
        this.showAction()
      } else {
        this._showTimer = setTimeout(this.showAction, 200)
        this.showIndex < this.maxDisplayIndex - 1 && this.$emit('setBlocker', true)
      }
    },
    showAction (index) {
      clearTimeout(this._showTimer)
      if (this.showIndex < this.maxDisplayIndex) {
        this.showIndex++
        if (this.disableTransition) {
          this.showAction()
        } else {
          this._showTimer = setTimeout(this.showAction, 200)
          this.$emit('setBlocker', true)
        }
      }
    },
    onCurrentNodeClick () {
      this.focusedSequenceIndex = this.focusedNodeIndex = 0
      this.$emit('onShowReport', 0, 0)
    },
    onNodeClick (e, sequenceIndex, nodeIndex) {
      this.focusedSequenceIndex = sequenceIndex
      this.focusedNodeIndex = nodeIndex
      this.$emit('onShowReport', sequenceIndex, nodeIndex)
    },
    onLookupOptionChange (item) {
      this.$emit('onLookupOptionChange', item)
    },
    generateGradient () {
      const $gradientCanvas = document.createElement('canvas')
      const gradientContext = $gradientCanvas.getContext('2d')
      $gradientCanvas.width = 512
      $gradientCanvas.height = 1
      const gradient = gradientContext.createLinearGradient(0, 0, 512, 0)
      gradient.addColorStop(0, this.isForwardDirection ? '#6e8b08' : '#11668c')
      gradient.addColorStop(0.8, this.isForwardDirection ? '#b8cf67' : '#b2d4e1')
      gradientContext.fillStyle = gradient
      gradientContext.fillRect(0, 0, 512, 1)
      this._gradientData = gradientContext.getImageData(0, 0, 512, 1).data
    },
    assignGradients () {
      this.gradientColors = []
      for (let sequenceIndex = 1, numSequences = this.maxDisplayIndex + 2; sequenceIndex < numSequences; sequenceIndex++) {
        const startIndex = Math.round(512 / numSequences * (sequenceIndex - 1)) * 4
        const endIndex = Math.round(512 / numSequences * sequenceIndex) * 4
        this.gradientColors[sequenceIndex] = [
          'rgb(' + this._gradientData[startIndex] + ' ' + this._gradientData[startIndex + 1] + ' ' + this._gradientData[startIndex + 2] + ')',
          'rgb(' + this._gradientData[endIndex] + ' ' + this._gradientData[endIndex + 1] + ' ' + this._gradientData[endIndex + 2] + ')'
        ]
      }
    },
    calculateNodePosition () {
      if (this.noData) {
        this.isInitialized = true
        return
      }

      const nodesDataArray = this.nodesDataArray
      const nodeWidthHalf = this.nodeWidth / 2
      const nodeHeightHalf = this.nodeHeight / 2
      const gridWidthHalf = this.gridWidth / 2
      const nodeHeightWithBottomMargin = this.nodeHeight + this.nodeBottomMargin + (this.numSelectedVisibleOptions * 21) + 4
      const numSequences = this.maxDisplayIndex + 1
      const currentNodeSessAndDropCnt = nodesDataArray[0][0].sessCnt + (nodesDataArray[0][0].dropCnt || 0)
      const currentNodeY = 95 + this.currentNodeHeight / 2
      const maxLineWidth = 70

      const moreRankHeight = this.moreRank ? nodeHeightWithBottomMargin : 0

      let listHeight = 0

      for (let sequenceIndex = 0, max = Math.min(numSequences + 1, this.nodesDataArray.length); sequenceIndex < max; sequenceIndex++) {
        const nodesArray = nodesDataArray[sequenceIndex]
        const x = this.getNodeX(sequenceIndex)
        const prevX = this.getNodeX(sequenceIndex - 1)
        const numNodesArray = nodesArray.length
        for (let nodeIndex = 0; nodeIndex < numNodesArray; nodeIndex++) {
          const nodeData = nodesArray[nodeIndex]
          nodeData._lineInfo = null
          const y = nodeData._y * nodeHeightWithBottomMargin

          if (sequenceIndex === 0) {
            nodeData._layout = { x: 170, y: 95, width: this.currentNodeWidth, height: this.currentNodeHeight }
          } else {
            nodeData._layout = { x: x, y: y, width: this.nodeWidth, height: this.nodeHeight }
            nodeData._lineInfo = {
              fromX: 0,
              fromY: nodeData._parent._layout.y + nodeHeightHalf,
              toX: nodeData._layout.x - prevX,
              toY: nodeData._layout.y + nodeHeightHalf,
              centerX: gridWidthHalf,
              offsetValue: nodeWidthHalf,
              radius: sequenceIndex === 1 ? 75 : 45,
              width: Math.min(60, nodeData.sessCnt / currentNodeSessAndDropCnt * maxLineWidth)
            }

            if (sequenceIndex === 1) {
              this.moreButtonInfo = {
                x: x,
                y: (nodeData._y + 1) * nodeHeightWithBottomMargin,
                width: this.nodeWidth,
                height: this.nodeHeight
              }
              const moreButtonPath = {
                cutted: true,
                fromX: 223,
                fromY: nodeData._y * nodeHeightWithBottomMargin - nodeHeightHalf,
                toX: x - prevX,
                toY: this.moreButtonInfo.y + nodeHeightHalf,
                centerX: gridWidthHalf,
                offsetValue: nodeWidthHalf,
                radius: 75,
                width: Math.min(3, nodeData.sessCnt / currentNodeSessAndDropCnt * maxLineWidth)
              }
              // nodeData._lineInfo
              moreButtonPath.toY = this.moreButtonInfo.y + nodeHeightHalf
              this.moreButtonPath = moreButtonPath
            }
          }

          listHeight = Math.max(listHeight, y + nodeHeightWithBottomMargin + moreRankHeight)
        }
      }

      for (let sequenceIndex = 0; sequenceIndex < numSequences; sequenceIndex++) {
        nodesDataArray[sequenceIndex].forEach((nodeData, nodeIndex) => {
          const children = nodeData._children
          const numChildren = children.length

          nodeData._totalLineWidth = 0
          for (let childIndex = 0; childIndex < numChildren; childIndex++) {
            nodeData._totalLineWidth += children[childIndex]._lineInfo.width
          }
          delete nodeData._dropPathInfo
          if (this.isForwardDirection && nodeData.dropCnt > 0) {
            const width = Math.max(3, Math.min(maxLineWidth, Math.ceil(nodeData.dropCnt / currentNodeSessAndDropCnt * maxLineWidth)))
            nodeData._dropPathInfo = {
              y: nodeData._layout.y + nodeData._layout.height / 2 - width / 2,
              width
            }
            if (sequenceIndex === 0 && nodesDataArray.length < 2) {
              nodeData._dropPathInfo.y = nodeData._layout.y + nodeData._layout.height - width - 15
            }
          }

          for (let childIndex = 0; childIndex < numChildren; childIndex++) {
            const childData = children[childIndex]
            childData._combinedLineInfo = null
            const childLineInfo = childData._lineInfo
            if (numChildren > 2) {
              if (childIndex === 1) {
                childData._combinedLineInfo = { ...childLineInfo, combined: true }
                childData._combinedLineInfo.toY = childLineInfo.toY - childLineInfo.radius
                childData._combinedLineInfo.width = nodeData._totalLineWidth - children[0]._lineInfo.width
              }
              if (childIndex > 0) {
                childLineInfo.fromX = children[1]._combinedLineInfo.centerX + children[1]._combinedLineInfo.width / 2 - childLineInfo.width / 2
                if (childIndex > 1) {
                  for (let p = 1; p < childIndex; p++) {
                    childLineInfo.fromX -= children[p]._lineInfo.width
                  }
                }
                childLineInfo.fromY = children[1]._combinedLineInfo.toY
                childLineInfo.cutted = true
                if (sequenceIndex < this.maxDisplayIndex && children[1]._display) {
                  childData._ignore = false
                }
                if (childIndex === 2) {
                  children[1]._combinedLineInfo.maxY = children[1]._combinedLineInfo.toY
                }
              }
            }
            if (sequenceIndex === 0) {
              childLineInfo.centerX = 230 - childLineInfo.width / 2
              if (childIndex === 0) {
                childLineInfo.fromY = currentNodeY
                childLineInfo.fromY -= (childData._parent._totalLineWidth - childLineInfo.width) / 2
                if (nodeData._dropPathInfo) {
                  childLineInfo.fromY -= nodeData._dropPathInfo.width / 2
                }
              } else if (childIndex === 1) {
                const prevSiblingLineInfo = children[childIndex - 1]._lineInfo
                const targetLineInfo = childData._combinedLineInfo || childData._lineInfo
                targetLineInfo.fromY = prevSiblingLineInfo.fromY + prevSiblingLineInfo.width / 2 + targetLineInfo.width / 2
                if (childData._combinedLineInfo) {
                  childLineInfo.centerX = 230 - childData._combinedLineInfo.width / 2
                  childLineInfo.fromX = childLineInfo.centerX + childData._combinedLineInfo.width / 2 - childLineInfo.width / 2
                  childData._combinedLineInfo.centerX = childLineInfo.centerX
                  childData._combinedLineInfo.offsetValue = childLineInfo.offsetValue
                  if (!childData._display) {
                    childData._combinedLineInfo.toY = 300
                  }
                }
              }
            } else if (childIndex === 0) {
              if (childData._display) {
                const adjustY = (nodeData._totalLineWidth - childLineInfo.width) / 2
                childLineInfo.fromY -= adjustY
                childLineInfo.toY -= adjustY
              } else {
                childLineInfo.width = nodeData._totalLineWidth
              }
              if (nodeData._dropPathInfo) {
                const adjustY = nodeData._dropPathInfo.width / 2
                childLineInfo.fromY -= adjustY
                childLineInfo.toY -= adjustY
              }
            } else if (childIndex === 1) {
              const prevSiblingLineInfo = children[childIndex - 1]._lineInfo
              const targetLineInfo = childData._combinedLineInfo || childData._lineInfo
              targetLineInfo.fromY = prevSiblingLineInfo.fromY + prevSiblingLineInfo.width / 2 + targetLineInfo.width / 2
              if (!childData._display) {
                targetLineInfo.toY = targetLineInfo.fromY + targetLineInfo.radius
              }
            }
          }

          if (nodeData._dropPathInfo && numChildren > 0) {
            nodeData._dropPathInfo.y = children[0]._lineInfo.fromY - children[0]._lineInfo.width / 2 + nodeData._totalLineWidth
          }
        })
      }

      this.svgStyles = []
      for (let sequenceIndex = 0; sequenceIndex < numSequences + 1; sequenceIndex++) {
        const style = {
          left: this.isForwardDirection ? this.getNodeX(sequenceIndex - 1) + 'px' : 'auto',
          right: this.isForwardDirection ? 'auto' : this.getNodeX(sequenceIndex - 1) + 'px',
          width: (sequenceIndex === 1 ? 425 - 170 + this.gridWidth / 2 : this.gridWidth) + 'px',
          height: Math.max(500, listHeight - this.nodeBottomMargin) + 'px',
          zIndex: this.numCroppedDisplayableSequences - sequenceIndex
        }
        this.svgStyles[sequenceIndex] = style
      }

      this.coverStyles = []
      const nodeMarginX = (this.gridWidth - this.nodeWidth) / 2
      for (let sequenceIndex = this.showIndex; sequenceIndex < numSequences; sequenceIndex++) {
        const style = {}
        let position, width
        if (sequenceIndex === 1) {
          position = 170
          width = this.gridWidth + 425 - position - nodeMarginX
        } else {
          position = (sequenceIndex - 1) * this.gridWidth + 425 - nodeMarginX - 50
          if (sequenceIndex === numSequences - 1) {
            width = this.gridWidth + 95
          } else {
            width = this.gridWidth + 100
          }
        }
        style.width = width + 'px'
        style[this.isForwardDirection ? 'left' : 'right'] = position + 'px'
        style.zIndex = this.numCroppedDisplayableSequences - sequenceIndex
        this.coverStyles[sequenceIndex] = style
      }

      listHeight = Math.max(listHeight, 300)
      this.listHeight = listHeight
      this.isInitialized = true

      requestAnimationFrame(() => {
        this.$emit('onNodeListResize')
      })

      this.assignGradients()
      this.show()
    },
    displayMoreData () {
      this.$emit('displayMoreData')
    },
    getNodeX (sequenceIndex) {
      return sequenceIndex === 0 ? 170 : sequenceIndex * this.gridWidth + 425 - this.gridWidth / 2
    },
    switchDirection () {
      this.$emit('onSwitchDirection')
    },
    onSettingButtonClick () {
      this.$emit('onSettingButtonClick')
    },
    onCoverAfterLeave (sequenceIndex) {
      if (this.showIndex === sequenceIndex) {
        this.$emit('setBlocker', false)
      }
    },
    onClickAiReport () {
      if (!this.ableToShowAiReport) return
      EventBus.$emit('setSegmentPayment')
    }
  }
}
</script>

<style lang="scss" scoped>
$content-start-x: 164px;
$current-node-content-width: 425px;
$wrap-padding-start: calc(var(--targeting-content-start-x) + #{$content-start-x});

.ba-targeting-list-wrap {
  position: relative;
  padding-left: $wrap-padding-start;
  padding-right: calc(var(--targeting-wrap-size-x) - var(--targeting-extra-padding-x) + var(--targeting-grid-width));
  box-sizing: border-box;
  z-index: 0;

  &.backward {
    margin-left: auto;
    padding-left: calc(var(--targeting-wrap-size-x) - var(--targeting-extra-padding-x) + var(--targeting-grid-width) + 52px);
    padding-right: $wrap-padding-start;

    .ba-targeting-list-bg {
      margin-left: auto;

      &:before {
        left: auto;
        right: -1px;
      }

      .grid-backgrounds {
        left: 0;
        right: $current-node-content-width;
        flex-direction: row-reverse;

        span {
          background-color: transparent;
          box-shadow: inset -1px 0 0 rgba(0, 0, 0, 0.1), -1px 0 0 rgba(0, 0, 0, 0.1);
          &.focused {
            background-color: rgba(46, 170, 214, 0.11);
            box-shadow: inset -1px 0 0 rgba(40, 138, 173, 0.26), -1px 0 0 rgba(40, 138, 173, 0.26);
          }
        }
      }
    }

    .ba-targeting-list {
      .switch-direction {
        left: auto;
        right: 20px;

        &.tablet {
          right: 55px;
        }
        &.phone {
          right: 70px;
        }
        &.all {
          right: 45px;
        }

        button {
          box-shadow: 0 4px 7px -3px rgba(0, 0, 0, 0.2), 0 1px 0 0 #88a913;
          background-color: #97bd12;

          &:not(:disabled):hover {
            box-shadow: 0 4px 7px -3px rgba(0, 0, 0, 0.2), 0 1px 0 0 #728D0F;
            background-color: #7E9E0F;
          }

          &:disabled {
            opacity: 0.3;
            cursor: default;
          }

          &:after {
            transform: scaleX(-1);
          }
        }
      }
      .targeting-info {
        left: auto;
        right: 66px;
        &.all {
          right: 79px;
        }
      }
      .targeting-info.empty {
        right: 108px;
      }
      .current-node {
        left: auto;
        right: 170px;
        transform: translateX(50%);

        .no-journey {
          left: auto;
          right: calc(100% + 156px);

          &:before {
            left: auto;
            right: -142px;
            background-position: 1px 0;
          }
        }

        &.tablet {
          .no-journey {
            right: calc(100% + 156px + 35px);
            &:before {
              right: calc(-142px - 35px);
            }
          }
        }

        &.phone {
          .no-journey {
            right: calc(100% + 156px + 50px);
            &:before {
              right: calc(-142px - 50px);
            }
          }
        }

        &.all {
          right: 183px;
        }
      }
      .current-node.empty {
        right: 212px;
      }
    }

    .targeting-paths {
      transform: scaleX(-1);
    }

    .more-button-wrap {
      left: auto;
      right: calc(100% + var(--targeting-grid-width) / 2);
      margin-left: 0;
      margin-right: -20px;

      .more-button {
        transform: scaleX(-1);
      }
    }
  }
  &.forward {
    .ba-targeting-list {
      .targeting-info {
        top: -23px;
        height: 106px;

        .ui_select {
          position: absolute;
          left: 6px;
          bottom: 6px;

          &.active /deep/ {
            .item--wrap {
              &,
              &:hover {
                box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0), 0 0 0 1px rgba(0, 0, 0, 0), 0 0 0 0 #0074ff;
              }
            }
          }

          & /deep/ {
            .item--wrap {
              font-size: 12px;
              font-weight: 500;
              line-height: 27px !important;
              padding-left: 8px;
              border: none;
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08), 0 0 0 0 #0074ff;
              transition: box-shadow 0.1s ease-out;

              &:hover {
                box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.25), 0 0 0 2px rgba(0, 0, 0, 0.08), 0 0 0 1px #0074ff;
              }

              .selected-item .icon {
                right: 6px;
                background-image: url(../../assets/img/icon_dropdown-small.svg);
              }

              .item__list {
                left: 0;
                top: 0;
                border: none;
                box-shadow: 0 12px 15px -6px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.16);
                overflow: visible;
              }
              .item__list-wrap {
                line-height: 27px;
                border-radius: inherit;
                overflow: visible;
              }
              .item__list-item {
                position: relative;
                height: 28px;
                padding-left: 8px;
                transition: background-color 0.15s ease-out;

                &:first-child {
                  border-top-left-radius: inherit;
                  border-top-right-radius: inherit;
                }

                &:last-child {
                  border-bottom-left-radius: inherit;
                  border-bottom-right-radius: inherit;
                }

                &.on {
                  background-image: url(../../assets/img/icon_check_green_size16.svg);
                  background-repeat: no-repeat;
                  background-position: calc(100% - 8px) 6px;

                  &:hover {
                    .info {
                      opacity: 1;
                    }
                  }

                  .info {
                    opacity: 0;
                    transition: opacity 0.15s ease-out;
                  }
                }

                .info {
                  position: absolute;
                  right: 0;
                  top: 0;
                  bottom: 0;
                  width: calc(17px + 7px * 2);
                  background: url(../../assets/img/common/icon--info-outline-white.svg) no-repeat 50% 50%;

                  &:hover {
                    .tooltip {
                      opacity: 1;
                      transform: translate3d(-50%, 0, 0);
                    }
                  }

                  .tooltip {
                    position: absolute;
                    left: calc(50% + 1px);
                    bottom: calc(100% + 4px);
                    font-size: 13px;
                    font-weight: normal;
                    text-align: center;
                    line-height: 1.46;
                    padding: 16px 20px;
                    border-radius: 8px;
                    background-color: #fff;
                    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
                    opacity: 0;
                    pointer-events: none;
                    transform: translate3d(-50%, 10px, 0);
                    transition: 0.15s ease-out;
                    transition-property: opacity, transform;

                    &:before {
                      position: absolute;
                      left: 50%;
                      top: 100%;
                      width: 8px;
                      height: 8px;
                      content: '';
                      margin: -5px 0 0 -5px;
                      border: 1px solid;
                      border-color: transparent transparent rgba(0, 0, 0, 0.25) rgba(0, 0, 0, 0.25);
                      background-color: #fff;
                      transform: rotate(-45deg);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
.ba-targeting-list-bg {
  position: relative;
  padding-top: calc(var(--targeting-extra-padding-y));
  box-sizing: border-box;
  background-color: #F4F4F1;

  &:before {
    content: '';
    position: absolute;
    left: -1px;
    top: 0;
    bottom: 0;
    border-left: 1px solid rgba(0, 0, 0, 0.1);
  }

  .grid-backgrounds {
    position: absolute;
    left: $current-node-content-width;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;

    span {
      width: var(--targeting-grid-width);
      background-color: transparent;
      box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.1), 1px 0 0 rgba(0, 0, 0, 0.1);
      transition: opacity 0.2s;

      &.focused {
        opacity: 1;
        background-color: rgba(181, 182, 0, 0.11);
        box-shadow: inset 1px 0 0 rgba(120, 99, 0, 0.17), 1px 0 0 rgba(120, 99, 0, 0.17);
      }
    }
  }
}

.ba-targeting-list {
  position: relative;
  pointer-events: auto;

  &.heatmap {
    //pointer-events: auto;
  }

  &.hovered {
    & .buttons {
      opacity: 1;
    }
  }

  .switch-direction {
    position: absolute;
    left: 20px;
    top: 142px;

    &.tablet {
      left: 55px;
      top: 160px;
    }

    &.phone {
      left: 70px;
      top: 160px;
    }

    &.all {
      left: 31px;
      top: 168px;
    }

    &:hover {
      .bubble {
        opacity: 1;
        transform: translate3d(-50%, 0, 0);
      }
    }

    button {
      width: 34px;
      height: 34px;
      text-indent: -999em;
      border-radius: 8px;
      box-shadow: 0 4px 7px -3px rgba(0, 0, 0, 0.2), 0 1px 0 0 #056D9B;
      background-color: #208EBF;
      transition: 0.2s ease-out;
      transition-property: background-color, box-shadow;
      pointer-events: auto;
      overflow: hidden;

      &:hover {
        box-shadow: 0 4px 7px -3px rgba(0, 0, 0, 0.2), 0 1px 0 0 #045B82;
        background-color: #1A77A0;
      }

      &:after {
        position: absolute;
        left: 7px;
        top: 7px;
        width: 20px;
        height: 20px;
        content: '';
        background: url('../../assets/img/targeting/icon--switch-direction.svg') no-repeat;
      }
    }
    .bubble {
      left: 50%;
      bottom: calc(100% + 9px);
      padding: 16px 24px;
      cursor: default;
      opacity: 0;
      pointer-events: none;
      transform: translate3d(-50%, 10%, 0);
      transition-delay: 0s !important;
      transition: 0.2s ease;
      transition-property: opacity, transform;

      & /deep/ {
        em {
          font-weight: 600;
          color: #7e36e0;
        }

        .arrow {
          width: 8px;
          height: 8px;
        }
      }
    }
  }

  .targeting-info {
    position: absolute;
    left: 66px;
    top: 11px;
    width: 208px;
    height: 72px;
    border-radius: 10px;
    background-color: #f8f8f8;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12), 0 3px 4px -2px rgba(0, 0, 0, 0.12);
    pointer-events: auto;

    h3 {
      height: 44px;
      font-size: 18px;
      font-weight: 500;
      color: #2f2f2f;
      line-height: 22px;
      padding: 11px 0 0 43px;
      border-radius: 10px 10px 0 0;
      box-sizing: border-box;
      border-bottom: 1px solid #d8d8d8;
      background: #fff url('../../assets/img/targeting/icon--targeting.svg') no-repeat 8px 8px;
      box-shadow: 0 0 3px rgba(0, 0, 0, 0.15);
    }

    .link-wrap {
      left: 7px;
      right: 7px;
      top: auto;
      top: 47px;
      color: #555;
      transform: none;
    }

    .setting-button {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 44px;
      text-indent: -999em;

      &:before {
        position: absolute;
        right: 6px;
        top: 11px;
        width: 24px;
        height: 24px;
        content: '';
        border-radius: 100%;
        background: rgba(0, 0, 0, 0);
        transition: background-color 0.2s ease-out;
      }

      &:hover:before {
        background: rgba(0, 0, 0, 0.06);
      }

      &:not(.is-mobile):hover .bubble {
        opacity: 1;
        transform: translate(50%, 0);
      }

      &:after {
        position: absolute;
        right: 33px;
        top: 16px;
        height: 14px;
        content: '';
        border-left: 1px solid #ebebeb;
      }

      .icon {
        position: absolute;
        right: 10px;
        top: 15px;
      }

      .bubble {
        left: auto;
        right: 19px;
        bottom: calc(100% + 2px);
        text-indent: 0;
        padding-left: 20px;
        padding-right: 20px;
        transform: translate(50%, 10px);
        opacity: 0;
        pointer-events: none;
        transition: transform 0.2s ease, opacity 0.2s ease;
      }
    }
    .ai-report-button {
      position: absolute;
      left: 2px;
      top: -8px;
      transform: translateY(-100%);
      display: flex;
      height: 32px;
      padding: 4px 10px 4px 6px;
      align-items: center;
      gap: 4px;
      border-radius: 5px;
      background-color: #fff;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 3px 4px -2px rgba(0, 0, 0, 0.12);
      box-sizing: border-box;
      color: #555;
      .text-contents {
        min-width: 41px;
        font-size: 13px;
        font-weight: 500;
        text-align: center;
      }
      &:hover {
        background-color: rgba(0, 0, 0, 0.06);
      }
      &:disabled {
        opacity: 0.3;
        pointer-events: none;
      }
    }
  }
  .targeting-info.empty {
    left: 108px;
  }

  .current-node {
    position: absolute;
    left: 170px;
    top: 95px;
    width: 200px;
    height: 125px;
    border-radius: 17px;
    background: #fff no-repeat 50% 50% / cover;
    box-shadow: 0 27px 31px -9px rgba(0, 0, 0, 0.4);
    cursor: pointer;
    transform: translateX(-50%);
    pointer-events: auto;
    z-index: 1;

    .buttons {
      position: absolute;
      top: 5px;
      left: 5px;
      z-index: 99;
      opacity: 0;
      transition: opacity 0.2s ease;

      .wrap {
      }

      button {
        border-radius: 4px;
        white-space: nowrap;
        position: relative;
        z-index: 100;
        padding: 3px 3px 3px 3px;
        text-align: left;
        transition: background-color 0.2s ease;

        .icon {
          vertical-align: middle;
        }

        .label {
          position: relative;
          top: -1px;
          z-index: 100;
          cursor: pointer;
          color: #ffffff;
          overflow: hidden;
          display: inline-block;
          max-width: 0;
          transition: max-width 0.2s ease, margin 0.2s ease;
          vertical-align: middle;
          margin: 0;
        }

        &.hovered {
          .label {
            max-width: 100px;
            margin: 0 5px 0 4px;
          }
        }

        &.heatmap {
          margin-right: 4px;
          background-color: #272727;
        }
      }
    }

    .tracking-label {
      position: absolute;
      top: 5px;
      left: -31px;
      z-index: 100;
    }

    &.tablet {
      width: 130px;
      height: 166px;

      .group-name {
        padding: 11px;
        span {
          max-height: calc(1.29em * 3);
          font-size: 19px;
          line-height: 1.32;
          -webkit-line-clamp: 3;
        }

        &.untagged {
          padding-top: 55px;
          .icon {
            left: 11px;
            top: 82px;
          }
        }
      }

      .ba-targeting-values {
        min-width: 120px;
      }

      .no-journey {
        left: calc(100% + 156px + 35px);
        &:before {
          left: calc(-142px - 35px);
          width: calc(130px + 30px);
        }
      }
    }

    &.all {
      width: 180px;
      height: 180px;
      border-radius: 100%;

      &:before, &:after {
        border-radius: 100%;
      }
      .thumbnail {
        border-radius: 100%;
      }

      &.bookmarked:after {
        background: url('../../assets/img/targeting/icon--bookmark-round.svg') no-repeat 9% -1%, url('../../assets/img/targeting/icon--targeting-round.svg') no-repeat 91% 101%;
      }

      .group-name {
        padding: 48px 20px;
        span {
          font-size: 20px;
          -webkit-line-clamp: 2;
        }

        &.untagged {
          padding-top: 48px;
          .icon {
            left: 20px;
            top: 76px;
          }
        }
      }

      .ba-targeting-values {
        min-width: 110px;
      }

      .no-journey {
        &:before {
          width: calc(130px + 50px);
        }
      }

      .buttons {
        top: 41px;
        left: 16px;
      }

      .tracking-label {
        top: 41px;
        left: -31px;
      }
    }

    &.phone {
      width: 100px;
      height: 166px;

      .group-name {
        padding: 11px;
        span {
          max-height: calc(1.29em * 4);
          font-size: 17px;
          -webkit-line-clamp: 4;
        }

        &.untagged {
          padding-top: 56px;
          .icon {
            left: 11px;
            top: 82px;
          }
        }
      }

      .ba-targeting-values {
        min-width: 90px;
      }

      .no-journey {
        left: calc(100% + 156px + 50px);
        &:before {
          left: calc(-142px - 50px);
          width: calc(130px + 50px);
        }
      }
    }

    &.active,
    &.hovered {
      &:before {
        box-shadow: inset 0 0 0 4px #fff, 0 0 0 4px #0074ff;
      }
      & .buttons {
        opacity: 1;
      }
    }

    &:before {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      content: '';
      border-radius: 17px;
      background-color: var(--current-node-cover-color);
      box-shadow: inset 0 0 0 4px #fff, 0 0 0 4px #333;
      z-index: 1;
      transition: background-color 0.3s ease-out, box-shadow 0.2s ease-out;
    }

    &:after {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      content: 'Bookmarked';
      text-indent: -999em;
      border-radius: 16px;
      background: url('../../assets/img/targeting/icon-targeting-current-large.svg') no-repeat 100% 100% / auto;
      overflow: hidden;
      z-index: 2;
    }
    &.bookmarked:after {
      background: url('../../assets/img/targeting/icon--bookmark-ribbon.svg') no-repeat 0 0 / 43px, url('../../assets/img/targeting/icon-targeting-current-large.svg') no-repeat 100% 100% / auto;
    }

    &.no-thumbnail {
      .thumbnail:before {
        width: inherit;
        height: inherit;
        content: '';
        border-radius: inherit;
        background: inherit;
        display: block;
        transform: scale(1.175);
        filter: blur(5px);
      }
      .thumbnail:after {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        content: '';
        background: url('../../assets/img/journey/icon_add_image.svg') no-repeat 50% 50% / 24px;
      }
    }

    .thumbnail {
      position: relative;
      width: 100%;
      height: 100%;
      border-radius: 15px;
      background: #fff no-repeat 0% 0% / cover;
      display: block;
      overflow: hidden;
    }

    .group-name {
      position: absolute;
      left: 3px;
      right: 3px;
      top: 3px;
      bottom: 3px;
      padding: 16px;
      border-radius: 15px;
      z-index: 1;

      span {
        height: 100%;
        max-height: calc(1.29em * 2);
        font-size: 21px;
        font-weight: bold;
        color: #fff;
        word-break: break-all;
        text-overflow: ellipsis;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
        line-height: 1.29;
        display: block;
        overflow: hidden;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        display: -webkit-box;
      }

      .icon {
        display: none;
      }

      &.untagged {
        padding-top: 29px;
        .icon {
          position: absolute;
          left: 16px;
          top: 60px;
          display: block;
        }
      }

      &-enter-active, &-leave-active {
        transition: opacity 0.3s ease-out;
      }
      &-enter, &-leave-to {
        opacity: 0;
      }
    }

    .ba-targeting-values {
      min-width: 170px;

      & /deep/ .item {
        font-size: 18px;
        margin-top: 11px;
        padding-left: 26px;

        &:first-child {
          margin-top: 18px;
        }

        &.session {
          font-size: 22px;
          line-height: 22px;

          .icon {
            top: 1px;
            background-image: url('../../assets/img/journey/session.svg');
          }
        }

        &.duration {
          .icon {
            background-image: url('../../assets/img/targeting/icon-hourglass-large.svg');
          }
        }
      }
    }

    .no-journey {
      position: absolute;
      left: calc(100% + 156px);
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      z-index: -2;

      &:before {
        position: absolute;
        left: -142px;
        top: 50%;
        width: 130px;
        height: 10px;
        content: '';
        margin-top: -5px;
        background-image: radial-gradient(#c1c1c1 33%, transparent 33.1%);
        background-size: 10px 10px;
        background-position: -1px 0px;
        background-repeat: repeat-x;
      }

      .ment {
        width: var(--targeting-grid-width);
        text-align: center;

        span {
          font-size: 13px;
          color: #555;
          line-height: 1.31;
          padding: 17px 20px;
          padding: calc(17px + 1.5px) calc(20px + 1.5px);
          border-radius: 13px;
          box-sizing: border-box;
          background-color: rgba(0, 0, 0, 0.1);
          background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='13' ry='13' stroke='%23FFF' stroke-width='6' stroke-dasharray='0.1, 9' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
          display: inline-block;
        }
      }

    }

    &.trackable {
      /deep/ .tracking-bar {
        bottom: -8px;
        width: 180px;
      }
      .tracking-box {
        position: absolute;
        left: 50%;
        top: calc(100% + 8px);
        min-width: 100px;
        box-sizing: border-box;
        transform: translateX(-50%);
        width: 181px;
        border-radius: 12px;
        background-color: #FFF;
        box-shadow: 0px 2px 4px -2px rgba(0, 0, 0, 0.2);
        &-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 8px 8px 0;
          .tracking-label-wrap {
            @include flexYcenter;
            gap: 8px;
            font-size: 18px;
            font-weight: 600;
            color: $brandColor;
            .tracking-label {
              position: static;
            }
          }
          .tracking-user {
            display: flex;
            justify-content: flex-end;
            align-items: flex-end;
            gap: 8px;
            margin-right: 3px;
            color: #444;
            &-label {
              font-size: 12px;
              font-weight: 500;
              padding-bottom: 2px;
            }
            &-value {
              @include textEllipsis;
              font-size: 16px;
              font-weight: 600;
            }
          }
        }

        .stop-tracking-button {
          @include flexXYcenter;
          position: absolute;
          top: -4px;
          right: -8px;
          width: 28px;
          height: 28px;
          background-color: #fff;
          border-radius: 100%;
          box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.20);
        }

        /deep/ .tracking-bar {
          position: relative;
          left: auto;
          bottom: auto;
          width: 100%;
          padding: 8px 0 0;
          transform: none;
          &-wrap {
            box-shadow: none;
            padding: 4px;
          }
        }
      }

      /deep/ .ba-targeting-values {
        top: calc(100% + 18px);
      }

      &.tracking {
        /deep/ .ba-targeting-values {
          top: calc(100% + 78px);
        }
      }
    }
  }
  .current-node.empty {
    left: 212px;

    &.tablet {
      &.ko {
        word-break: keep-all;
      }
      .ment {
        padding: 0 10px;
      }
    }

    &.phone {
      &.ko {
        word-break: keep-all;
      }
      .ment {
        font-size: 12px;
        line-height: 1.33;
        padding: 0 10px;
      }
    }

    &:before {
      box-shadow: inset 0 0 0 4px #fff;
    }

    &:after {
      content: none;
    }

    .ment {
      position: absolute;
      left: -4px;
      right: -4px;
      top: -4px;
      bottom: -4px;
      font-size: 14px;
      color: #666;
      text-align: center;
      line-height: 1.29;
      border-radius: 19px;
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='19' ry='19' stroke='%23333' stroke-width='8' stroke-dasharray='0.1, 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
      align-items: center;
      justify-content: center;
      display: flex;
    }

    .info {
      position: absolute;
      left: 9px;
      top: 9px;
      z-index: 1;

      .icon {
        width: 17px;
        height: 17px;
        border-radius: 100%;
        background-color: #fff;
      }

      .icon:hover + .bubble {
        opacity: 1;
        transform: translate3d(-50%, 0, 0);
      }

      .bubble {
        left: 50%;
        bottom: calc(100% + 9px);
        width: 205px;
        padding: 16px 20px;
        box-sizing: border-box;
        opacity: 0;
        pointer-events: none;
        cursor: default;
        transform: translate3d(-50%, 10%, 0);
        transition-delay: 0s !important;
        transition: 0.2s ease;
        transition-property: opacity, transform;

        & /deep/ {
          .text {
            white-space: normal;
            text-align: left;
          }
        }
      }
    }
  }

  .targeting-paths-wrap {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    overflow: hidden;
  }
  .targeting-paths {
    position: absolute;
    top: 0;

    g {
      fill: none;
    }
    path {
      fill: none;
      transition: all 0.45s $ease-out-expo;
    }
  }
}

.appear-line-enter-active,
.appear-node-enter-active {
  transition: transform 0.6s $ease-out-cubic;
}
.appear-line-enter,
.appear-node-enter {
  transform: translate3d(-50px, 0, 0) !important;
}
.ba-targeting-list-wrap.backward {
  .appear-line-enter {
    transform: scaleX(-1) translate3d(-50px, 0, 0) !important;
  }
  .appear-node-enter {
    transform: translate3d(50px, 0, 0) !important;
  }
}

.more-seq-wrap {
  cursor: pointer;
  position: absolute;
  top: 0;
  left: auto;
  right: 100%;
  width: 200px;
  height: 100%;
  background: color-mix(in srgb, #FACBA1 3%, #f4f4f4);
  &:hover {
    .more-btn-background  {
      opacity: 1;
    }
  }
  .more-btn-background {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: rgba(46, 170, 214, 0.11);
    box-shadow: inset -1px 0 0 rgba(40, 138, 173, 0.26), -1px 0 0 rgba(40, 138, 173, 0.26);
    opacity: 0;
  }
  .more-seq-btn{
    position: absolute;
    top: 0;
    left: 50%;
    width: 68px;
    height: 68px;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 13px;
    // background: #FFF;
    color: #0E90A5;
    // box-shadow: 0px 2px 4px -2px rgba(0, 0, 0, 0.24);
  }
}
.forward .more-seq-wrap {
  left: 100%;
  right: auto;
  .more-btn-background {
    background-color: rgba(181, 182, 0, 0.11);
    box-shadow: inset 1px 0 0 rgba(120, 99, 0, 0.17), 1px 0 0 rgba(120, 99, 0, 0.17);
  }
  .more-seq-btn {
    color: #6E8B08;
  }
}
.more-button-wrap{
  position: absolute;
  left: calc(100% + var(--targeting-grid-width) / 2);
  top: 0;
  margin: calc(-20px + 32px / 2) 0 0 -20px;

  &.disabled {
    .more-button {
      opacity: 0.3;
      pointer-events: none;
    }

    .no-more {
      .icon {
        position: absolute;
        right: -7px;
        top: -7px;
      }
      span {
        position: absolute;
        left: 50%;
        bottom: 100%;
        font-size: 13px;
        color: #444;
        white-space: nowrap;
        line-height: 1.46;
        padding: 16px 20px;
        border-radius: 8px;
        background-color: #fff;
        box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
        transform: translate(-50%, -15px);
      }
    }
  }

  .more-button {
    width: 41px;
    height: 41px;
    padding: 8px;
    transition: box-shadow 0.2s ease;
    background-color: #ffffff;
    box-shadow: 0 5px 6px -2px rgba(0, 0, 0, 0.22), 0 0 0 1px rgba(0, 0, 0, 0.15);
    border-radius: 21px;

    &:hover {
      box-shadow: 0 5px 6px -2px rgba(0, 0, 0, 0.22), 0 0 0 2px rgba(0, 116, 255, 1);
    }
  }
}

.more-rank-btn {
  cursor: pointer;
  position: absolute;
  z-index: 100;
  border-radius: 13px;
  border: 3px solid #0E90A5;
  background: #FFF;
  box-shadow: 0px 15px 11px -8px rgba(0, 0, 0, 0.16);
  transform: translateX(50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: #0E90A5;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  box-sizing: border-box;
  &.all {
    border-radius: 100%;
  }
}

.forward .more-rank-btn {
  border-color: #6E8B08;
  color: #6E8B08;
  transform: translateX(-50%);
}

.cover {
  position: absolute;
  top: -50px;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  background: #F4F4F1;
  z-index: 30;
  pointer-events: auto;

  &-leave-active {
    transition: opacity 0.5s ease-in-out;
  }
  &-leave-to {
    opacity: 0;
  }
}
</style>
